import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiArrowRight } = FiIcons;

const Navbar = () => {
  const { lang, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const navLinks = [
    { path: '/ai-lead-agent', label: lang === 'es' ? 'Agente IA' : 'AI Agent' },
    { path: '/content-shooting', label: lang === 'es' ? 'Contenido' : 'Content' },
    { path: '/insights', label: 'Insights' }
  ];

  const TIDYCAL_URL = "https://tidycal.com/mathiasdeppe/free-15-minute-growth-review";

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 w-full z-[100] px-6 py-4 md:px-12 md:py-6 flex justify-between items-center bg-zinc-950/80 backdrop-blur-lg border-b border-white/5"
      >
        <div className="flex items-center gap-8">
          {/* Clickable Logo */}
          <Link to="/" className="text-xl md:text-2xl tracking-tighter text-white hover:opacity-80 transition-opacity flex items-center z-[110]">
            <span className="font-normal">Mathias</span>
            <span className="font-bold ml-1.5">Deppe</span>
            <span className="text-orange-500">.</span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs font-black uppercase tracking-widest transition-colors ${
                  location.pathname === link.path ? 'text-orange-500' : 'text-zinc-500 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 md:gap-6">
          {/* Language Switcher */}
          <button
            onClick={toggleLanguage}
            className="text-xs font-bold tracking-widest text-zinc-500 hover:text-white transition-colors flex items-center gap-2"
          >
            <span className={lang === 'en' ? 'text-orange-500' : ''}>EN</span>
            <span className="w-[1px] h-3 bg-zinc-800" />
            <span className={lang === 'es' ? 'text-orange-500' : ''}>ES</span>
          </button>

          {/* Desktop CTA */}
          <a
            href={TIDYCAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-zinc-950 bg-white rounded-full hover:bg-zinc-200 transition-colors duration-300"
          >
            {t.nav.cta}
          </a>

          {/* Mobile Hamburger Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white bg-zinc-900 border border-white/10 rounded-xl z-[110]"
            aria-label="Toggle Menu"
          >
            <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-xl" />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[90] bg-zinc-950 lg:hidden flex flex-col pt-32 px-8 pb-12"
          >
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[500px] bg-orange-500/10 blur-[120px] pointer-events-none" />

            <div className="flex flex-col gap-8 relative z-10">
              <span className="text-zinc-600 font-black text-[10px] uppercase tracking-[0.3em] mb-2">Navigation</span>
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`text-4xl font-black uppercase tracking-tighter block transition-colors ${
                      location.pathname === link.path ? 'text-orange-500' : 'text-white hover:text-orange-500'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="mt-auto relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <a
                  href={TIDYCAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-between p-6 bg-white text-zinc-950 font-black rounded-3xl hover:bg-orange-500 hover:text-white transition-all group"
                >
                  <span className="text-xl uppercase tracking-tighter">{t.nav.cta}</span>
                  <SafeIcon icon={FiArrowRight} className="text-2xl group-hover:translate-x-2 transition-transform" />
                </a>
              </motion.div>

              <div className="mt-8 flex justify-between items-center text-zinc-600 text-[10px] font-bold uppercase tracking-widest">
                <span>© {new Date().getFullYear()} MATHIAS DEPPE</span>
                <div className="flex gap-4">
                  <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                  <Link to="/imprint" className="hover:text-white transition-colors">Imprint</Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;